import React, { useReducer, useState } from "react";
import {
  Select,
  Input,
  Button,
  message,
  Row,
  Col,
  Space,
  Typography,
  Form,
  Divider,
  Table,
} from "antd";
import {
  CheckCircleOutlined,
  CheckOutlined,
  ClearOutlined,
  CopyOutlined,
  DeleteOutlined,
  ExportOutlined,
  MinusCircleOutlined,
  PlusCircleOutlined,
  ReloadOutlined,
} from "@ant-design/icons";

const { Option } = Select;
const { TextArea } = Input;
const { Title } = Typography;

type JsonValue = string | number | boolean | null | JsonObject | JsonValue[];
interface JsonObject {
  [key: string]: JsonValue;
}

const defaultConfigs: Record<string, JsonObject> = {
  Text: { placeholder: "", helpText: "", toolTip: "" },
  Number: { placeholder: "", helpText: "", toolTip: "" },
  Date: { placeholder: "", helpText: "", toolTip: "", defaultValue: "[Today]" },
  Radio: {
    placeholder: "Select Gender",
    helpText: "Gender",
    toolTip: "Gender",
    maxSelection: 1,
    dependentField: "",
    options: [
      { code: "Male", label: "Male", value: "1" },
      { code: "Female", label: "Female", value: "2" },
    ],
    datasource: { execType: "", execData: "", execParams: [{ name: "" }] },
  },
  SelectSingle: {
    placeholder: "Select Gender",
    helpText: "State",
    toolTip: "State",
    maxSelection: 1,
    lazyload: false,
    dependentFieldId: "",
    defaultOption: "[Me]",
    options: [
      { code: "Male", label: "Male", value: "1" },
      { code: "Female", label: "Female", value: "2" },
    ],
    datasource: { execType: "", execData: "", execParams: [{ name: "" }] },
  },
  SelectMulti: {
    placeholder: "Select Multi Select",
    helpText: "MultiSelect",
    toolTip: "Multi Select",
    lazyload: false,
    dependentFieldId: "",
    defaultOption: "[Me]",
    options: [
      { code: "M-option1", label: "M-option1", value: "1" },
      { code: "M-option2", label: "M-option2", value: "2" },
      { code: "M-option3", label: "M-option3", value: "3" },
    ],
    datasource: { execType: "", execData: "", execParams: [{ name: "" }] },
  },
  File: {
    name: "",
    acceptedFileTypes: "",
    maxFileCount: 1,
  },
  Checkbox: {
    helpText: "",
    toolTip: "",
    defaultValue: "",
  },
  CheckboxMulti: {
    placeholder: "Select Interests",
    helpText: "Interests",
    toolTip: "Interests",
    layout: "horizontal",
    defaultValue: "music",
    maxSelection: 1,
    dependentField: "",
    options: [
      { code: "Music", label: "Music", value: "music" },
      { code: "Sports", label: "Sports", value: "sports" },
      { code: "Reading", label: "Reading", value: "reading" },
    ],
    datasource: { execType: "", execData: "", execParams: [{ name: "" }] },
  },
  TextArea: {
    placeholder: "",
    helpText: "",
    toolTip: "",
    rows: 4,
    maxLength: 100,
  },
};

const setValueByPath = (obj: JsonObject, path: string, value: any): void => {
  const keys = path.split(".");
  let current = obj;
  keys.forEach((key, index) => {
    if (index === keys.length - 1) {
      current[key] = value;
    } else {
      current[key] = current[key] ?? {};
      current = current[key] as JsonObject;
    }
  });
};

interface ArrayFieldEditorProps {
  data: any[];
  path: string;
  onChange: (path: string, value: any[]) => void;
}

const ArrayFieldEditor: React.FC<ArrayFieldEditorProps> = ({
  data,
  path,
  onChange,
}) => {
  const [, forceUpdate] = useReducer((x) => x + 1, 0);

  const handleItemChange = (index: number, key: string, value: string) => {
    const updated = [...data];
    updated[index][key] = value;
    onChange(path, updated);
    forceUpdate();
  };

  const handleAddItem = () => {
    const newItem: Record<string, string> = {};
    if (data.length > 0) {
      Object.keys(data[0]).forEach((key) => {
        newItem[key] = "";
      });
    }
    onChange(path, [...data, newItem]);
    forceUpdate();
  };

  const handleRemoveItem = (index: number) => {
    const updated = data.filter((_, i) => i !== index);
    onChange(path, updated);
    forceUpdate();
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <Title level={5}>
        {path.split(".").slice(-1)[0]}{" "}
        <span>
          (
          {path.split(".").slice(-1)[0] == "options"
            ? "Add field options here incase of fixed options."
            : "Parameters to be sent to function. Parameter name should match field backend name"}
          )
        </span>
      </Title>
      {data.map((item, index) => (
        <Row gutter={8}
          key={index} // NOSONAR : ignore
          style={{ marginBottom: 8 }}>
          {Object.keys(item).map((key) => (
            <Col span={6} key={key}>
              <Input
                value={item[key]}
                onChange={(e) => handleItemChange(index, key, e.target.value)}
                placeholder={key}
              />
            </Col>
          ))}
          <Col>
            <Button
              variant="dashed"
              color="danger"
              icon={<DeleteOutlined />}
              onClick={() => handleRemoveItem(index)}
            >
              Remove
            </Button>
          </Col>
        </Row>
      ))}
      <Button
        variant="dashed"
        color="primary"
        icon={<PlusCircleOutlined />}
        onClick={handleAddItem}
      >
        Add Item
      </Button>
      <Divider />
      <Title level={5}>
        <span>
          {path.split(".").slice(-1)[0] == "options"
            ? "Add source for dynamic options here."
            : ""}
        </span>
      </Title>
    </div>
  );
};

const FieldConfigBuilder: React.FC = () => {
  const [fieldType, setFieldType] = useState<string>("");
  const [jsonData, setJsonData] = useState<JsonObject>({});
  const [jsonText, setJsonText] = useState<string>("");
  const [form] = Form.useForm();

  const handleLoadJson = () => {
    if (fieldType && defaultConfigs[fieldType]) {
      const json = defaultConfigs[fieldType];
      setJsonData(json);
      setJsonText(JSON.stringify(json, null, 2));
    } else {
      message.error("No default config found for selected field type.");
    }
  };

  const handleSaveJson = () => {
    try {
      const parsed = JSON.parse(jsonText);
      setJsonData(parsed);
      defaultConfigs[fieldType] = parsed;
      message.success("Saved");
    } catch {
      message.error("Invalid JSON");
    }
  };

  const handleClear = () => {
    setJsonData({});
    setJsonText("");
    form.resetFields();
  };

  const handleBeautify = () => {
    try {
      const cleanedData = cleanEmptyValues(JSON.parse(jsonText));
      const beautified = JSON.stringify(cleanedData, null, 2);
      setJsonText(beautified);
    } catch (e) {
      console.error("Invalid JSON:", e);
      message.error("Invalid JSON format");
    }
  };

  const handleMinify = () => {
    try {
      const cleanedData = cleanEmptyValues(JSON.parse(jsonText));
      const minified = JSON.stringify(cleanedData);
      setJsonText(minified);
    } catch (e) {
      console.error("Invalid JSON:", e);
      message.error("Invalid JSON format");
    }
  };

  const handleCopy = () => {
    try {
      const cleanedData = cleanEmptyValues(JSON.parse(jsonText));
      const cleanedJson = JSON.stringify(cleanedData);
      navigator.clipboard
        .writeText(cleanedJson)
        .then(() => message.success("Copied to clipboard!"))
        .catch(() => message.error("Failed to copy JSON"));
    } catch (e) {
      console.error("Invalid JSON:", e);
      message.error("Invalid JSON format");
    }
  };

  // validate JSON
  const validateJson = () => {
    try {
      const cleanedData = cleanEmptyValues(JSON.parse(jsonText));
      message.success("JSON is in correct format!");
      return JSON.stringify(cleanedData, null, 2);
    } catch (e) {
      console.error("Invalid JSON:", e);
      message.error("Invalid JSON format");
      return jsonText;
    }
  };

  // Update Query JSON
  const updateQueryJsonInput = () => {
    try {
      const cleanedData = cleanEmptyValues(JSON.parse(jsonText));
      const stringified = JSON.stringify(cleanedData);
      const query = `Update Field SET Config = '${stringified}' Where Id = ??`;
      navigator.clipboard
        .writeText(query)
        .then(() => {
          message.success("JSON copied to clipboard!");
        })
        .catch(() => {
          message.error("Failed to copy text!");
        });
    } catch (e) {
      console.error("Invalid JSON:", e);
      message.error("Invalid JSON format");
      return jsonText; // Return original if parsing fails
    }
  };

  // Remove json parameter if value empty
  const cleanEmptyValues = (obj: JsonObject): JsonObject => {
    return Object.fromEntries(
      Object.entries(obj).filter(([key, value]) => {
        // Remove properties where value is empty or null
        if (
          value === "" ||
          value === null ||
          (Array.isArray(value) && value.length === 0)
        ) {
          return false;
        }

        // Handle specific fields like `datasource`
        if (key === "datasource") {
          const { execType, execData } = value as JsonObject;
          if (!execType && !execData) {
            return false; // Remove 'datasource' if all relevant fields are empty
          }
        }
        return true; // Keep the property
      })
    );
  };

  // Placeholder field wise
  const getPlaceholderByFieldType = (
    fieldType: string,
    key: string
  ): string => {
    switch (fieldType) {
      case "Text":
        return key === "placeholder" ? "Input Placeholder" : `Enter ${key}`;
      case "Number":
        return key === "placeholder" ? "Enter number" : `Enter ${key}`;
      case "Date":
        return key === "placeholder" ? "Enter date label" : `Enter ${key}`;
      case "Radio":
      case "SelectSingle":
      case "SelectMulti":
      case "CheckboxMulti":
        return key === "placeholder" ? "Select an option" : `Enter ${key}`;
      case "TextArea":
        return key === "placeholder"
          ? "Enter detailed text here"
          : `Enter ${key}`;
      case "File":
        return key === "name" ? "Upload file name" : `Enter ${key}`;
      case "Checkbox":
        return key === "defaultValue" ? "true / false" : `Enter ${key}`;
      default:
        return `Enter ${key}`;
    }
  };

  // Note field wise
  const getNoteByFieldType = (
    fieldType: string,
    key: string
  ): React.ReactNode => {
    const boldNote = (text: string) => (
      <span>
        <strong>Note:</strong> {text}
      </span>
    );

    // 🔹 Common note mappings across multiple field types
    const commonNotes: Record<string, string> = {
      execType: "Select whether to execute SP/Query or Function",
      execData: "Take text from interface builder",
      label: "Default Option",
    };

    // 🔹 Field-type–specific mappings
    const fieldNotes: Record<string, Record<string, string>> = {
      Date: {
        defaultValue:
          "Default value for field. Can be set as [Today] or [FieldName]",
      },
      Radio: {
        dependentField: "Field Id which are dependent on this field",
      },
      SelectSingle: {
        dependentFieldId: "Field Id which are dependent on this field",
        lazyload:
          "Set to true if field options is to be fetched post form load",
        defaultOption: "Default value for field. Can be set as [Me]",
      },
      SelectMulti: {
        dependentFieldId: "Field Id which are dependent on this field",
        lazyload:
          "Set to true if field options is to be fetched post form load",
        defaultOption: "Default value for field. Can be set as [Me]",
      },
      CheckboxMulti: {
        dependentField: "Field Id which are dependent on this field",
      },
    };

    // 🔹 Skip types that have no notes at all
    if (["Text", "Number"].includes(fieldType)) return "";

    // 🔹 Check if there’s a specific note for this field + key
    const specificNote = fieldNotes[fieldType]?.[key];
    if (specificNote) return boldNote(specificNote);

    // 🔹 Otherwise, fall back to common notes (if any)
    const commonNote = commonNotes[key];
    if (commonNote) return boldNote(commonNote);

    return "";
  };

  const handleJsonFieldChange = (path: string, value: string | any[]) => {
    const updated = JSON.parse(JSON.stringify(jsonData));
    setValueByPath(updated, path, value);
    setJsonData(updated);
    setJsonText(JSON.stringify(updated, null, 2));
  };

  const renderEditor = (obj: JsonObject, parentPath = ""): React.ReactNode => {
    const rows: { key: string; label: string; value: string }[] = [];
    const nestedEditors: React.ReactNode[] = [];

    Object.entries(obj).forEach(([key, value]) => {
      const fullPath = parentPath ? `${parentPath}.${key}` : key;
      if (
        Array.isArray(value) &&
        value.length > 0 &&
        typeof value[0] === "object"
      ) {
        nestedEditors.push(
          <Col span={24} key={fullPath}>
            <ArrayFieldEditor
              data={value}
              path={fullPath}
              onChange={handleJsonFieldChange}
            />
          </Col>
        );
      } else if (typeof value === "object" && value !== null) {
        nestedEditors.push(
          <Col span={24} key={fullPath}>
            {renderEditor(value as JsonObject, fullPath)}
          </Col>
        );
      } else {
        rows.push({
          key: fullPath,
          label: key,
          value: value as string,
        });
      }
    });
    return (
      <div
        style={
          parentPath
            ? { border: "1px dashed #999", padding: 16, marginBottom: 16 }
            : {}
        }
      >
        <Row gutter={16}>
          {rows.length > 0 && (
            <Col lg={24} md={24} sm={24} xs={24}>
              <Table
                scroll={{ x: "max-content" }}
                dataSource={rows}
                pagination={false}
                bordered={true}
                columns={[
                  {
                    title: "Field",
                    dataIndex: "label",
                    key: "label",
                  },
                  {
                    title: "Value",
                    dataIndex: "value",
                    key: "value",
                    render: (text, record) => (
                      <>
                        {record.label === "execType" ? (
                          <Select
                            value={text}
                            onChange={(newValue) =>
                              handleJsonFieldChange(record.key, newValue)
                            }
                            style={{ width: "100%" }}
                          >
                            <Select.Option value="">Select</Select.Option>
                            <Select.Option value="SP">SP</Select.Option>
                            <Select.Option value="Query">Query</Select.Option>
                            <Select.Option value="Function">
                              Function
                            </Select.Option>
                          </Select>
                        ) : (
                          <Input
                            value={text}
                            onChange={(e) =>
                              handleJsonFieldChange(record.key, e.target.value)
                            }
                            placeholder={getPlaceholderByFieldType(
                              fieldType,
                              record.label
                            )}
                          />
                        )}
                        <span>
                          {getNoteByFieldType(fieldType, record.label)}
                        </span>
                      </>
                    ),
                  },
                ]}
              />
            </Col>
          )}
          {nestedEditors}
        </Row>
      </div>
    );
  };

  return (
    <div>
      <h4>Field config JSON builder</h4>
      <Row gutter={16}>
        <Col lg={6} md={24} sm={24} xs={24}>
          <label htmlFor="fieldType">Field Type</label>
          <Select
            id="fieldType"
            value={fieldType}
            onChange={setFieldType}
            showSearch
            style={{ width: "100%" }}
          >
            <Option value="">Select field Type</Option>
            {Object.keys(defaultConfigs).map((type) => (
              <Option key={type} value={type}>
                {type}
              </Option>
            ))}
          </Select>
        </Col>
        <Button
          onClick={handleLoadJson}
          color="cyan"
          variant="solid"
          icon={<CheckOutlined />}
          style={{ marginTop: 20 }}
        >
          Get JSON
        </Button>
      </Row>

      <Row style={{ marginTop: 16 }}>
        <Col lg={24} md={24} sm={24} xs={24}>
          <Form.Item>
            <TextArea
              placeholder="Paste JSON here..."
              value={jsonText}
              onChange={(e) => setJsonText(e.target.value)}
              rows={10}
            />
            <Col span={24} style={{ marginTop: 10 }}>
              <Space wrap>
                <Button
                  onClick={handleSaveJson}
                  color="primary"
                  variant="solid"
                  icon={<ReloadOutlined />}
                >
                  Load
                </Button>
                <Button
                  danger
                  onClick={handleClear}
                  color="danger"
                  variant="solid"
                  icon={<ClearOutlined />}
                >
                  Clear
                </Button>
                <Button
                  onClick={handleMinify}
                  color="orange"
                  variant="solid"
                  icon={<MinusCircleOutlined />}
                >
                  Minify
                </Button>
                <Button
                  onClick={handleBeautify}
                  color="purple"
                  variant="solid"
                  icon={<ExportOutlined />}
                >
                  Beautify
                </Button>
                <Button
                  onClick={handleCopy}
                  color="pink"
                  variant="solid"
                  icon={<CopyOutlined />}
                >
                  Copy JSON
                </Button>
                <Button
                  onClick={validateJson}
                  color="danger"
                  variant="solid"
                  icon={<CheckCircleOutlined />}
                >
                  Validate JSON
                </Button>
                <Button
                  onClick={updateQueryJsonInput}
                  color="cyan"
                  variant="solid"
                  icon={<CheckOutlined />}
                >
                  Copy JSON Query
                </Button>
              </Space>
            </Col>
          </Form.Item>
        </Col>
      </Row>

      <Row style={{ marginTop: 16 }}>
        <Col lg={24} md={24} sm={24} xs={24}>
          <Form layout="vertical" form={form}>
            <Row gutter={16}>{renderEditor(jsonData)}</Row>
          </Form>
        </Col>
      </Row>
    </div>
  );
};

export default FieldConfigBuilder;
